import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { getAdminSpeakers, createSpeaker, updateSpeaker, deleteSpeaker } from '../../api/client';
import { toast } from 'sonner';
import { Plus, Trash2, Edit3, User, Save, X, Upload, GripVertical, Eye, EyeOff } from 'lucide-react';
import { useSettings } from '../../contexts/SettingsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const emptySpeaker = { name: '', title: '', bio: '', photo: '', sub_event: '', company: '', country: '', order: 0, visible: true };

export const AdminSpeakers = () => {
  const { settings } = useSettings();
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptySpeaker);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const subEventOptions = [
    settings.subEventTheme1 || 'Halal Business Forum',
    settings.subEventTheme2 || 'Women Entrepreneurs Summit'
  ].filter(Boolean);

  const fetchSpeakers = useCallback(async () => {
    try {
      const data = await getAdminSpeakers();
      setSpeakers(data.speakers || []);
    } catch {
      toast.error('Failed to load speakers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSpeakers(); }, [fetchSpeakers]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/admin/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      setForm(f => ({ ...f, photo: data.url }));
      toast.success('Photo uploaded');
    } catch {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Speaker name is required'); return; }
    try {
      if (editing) {
        await updateSpeaker(editing, form);
        toast.success('Speaker updated');
      } else {
        await createSpeaker({ ...form, order: speakers.length });
        toast.success('Speaker added');
      }
      setEditing(null);
      setForm(emptySpeaker);
      setShowForm(false);
      fetchSpeakers();
    } catch {
      toast.error('Failed to save speaker');
    }
  };

  const handleEdit = (speaker) => {
    setForm({
      name: speaker.name || '', title: speaker.title || '', bio: speaker.bio || '',
      photo: speaker.photo || '', sub_event: speaker.sub_event || '',
      company: speaker.company || '', country: speaker.country || '',
      order: speaker.order || 0, visible: speaker.visible !== false
    });
    setEditing(speaker.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this speaker?')) return;
    try {
      await deleteSpeaker(id);
      toast.success('Speaker deleted');
      fetchSpeakers();
    } catch {
      toast.error('Failed to delete speaker');
    }
  };

  const toggleVisibility = async (speaker) => {
    try {
      await updateSpeaker(speaker.id, { visible: !speaker.visible });
      fetchSpeakers();
    } catch {
      toast.error('Failed to update');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-green-600 border-r-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-speakers-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Speakers</h2>
          <p className="text-gray-500 mt-1">{speakers.length} speaker{speakers.length !== 1 ? 's' : ''} configured</p>
        </div>
        <Button
          data-testid="add-speaker-btn"
          onClick={() => { setForm(emptySpeaker); setEditing(null); setShowForm(true); }}
          className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
        >
          <Plus size={16} /> Add Speaker
        </Button>
      </div>

      {showForm && (
        <Card className="border-green-200 border-2" data-testid="speaker-form">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center justify-between">
              {editing ? 'Edit Speaker' : 'Add New Speaker'}
              <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); setEditing(null); }}>
                <X size={18} />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Full Name *</Label>
                <Input data-testid="speaker-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Dr. John Smith" className="mt-1" />
              </div>
              <div>
                <Label>Title / Position</Label>
                <Input data-testid="speaker-title-input" value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="CEO, Industry Expert" className="mt-1" />
              </div>
              <div>
                <Label>Company / Organization</Label>
                <Input value={form.company} onChange={e => setForm({...form, company: e.target.value})} placeholder="Organization name" className="mt-1" />
              </div>
              <div>
                <Label>Country</Label>
                <Input value={form.country} onChange={e => setForm({...form, country: e.target.value})} placeholder="Indonesia" className="mt-1" />
              </div>
              <div>
                <Label>Sub-Event</Label>
                <select
                  data-testid="speaker-subevent-select"
                  value={form.sub_event}
                  onChange={e => setForm({...form, sub_event: e.target.value})}
                  className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-md text-sm"
                >
                  <option value="">— Select sub-event —</option>
                  {subEventOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                  <option value="Keynote">Keynote</option>
                </select>
              </div>
              <div>
                <Label>Display Order</Label>
                <Input type="number" value={form.order} onChange={e => setForm({...form, order: parseInt(e.target.value) || 0})} className="mt-1" />
              </div>
            </div>

            <div>
              <Label>Bio</Label>
              <textarea
                value={form.bio}
                onChange={e => setForm({...form, bio: e.target.value})}
                placeholder="Brief biography..."
                rows={3}
                className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-md text-sm resize-none"
              />
            </div>

            <div>
              <Label>Photo</Label>
              <div className="flex items-center gap-4 mt-1">
                {form.photo ? (
                  <img src={form.photo} alt="Preview" className="w-16 h-16 rounded-full object-cover border-2 border-gray-200" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                    <User size={24} className="text-gray-400" />
                  </div>
                )}
                <div className="flex gap-2">
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                    <span className="inline-flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-sm font-medium text-gray-700 transition">
                      <Upload size={14} /> {uploading ? 'Uploading...' : 'Upload Photo'}
                    </span>
                  </label>
                  <Input
                    value={form.photo}
                    onChange={e => setForm({...form, photo: e.target.value})}
                    placeholder="Or paste image URL"
                    className="flex-1 text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.visible} onChange={e => setForm({...form, visible: e.target.checked})} className="w-4 h-4 text-green-600 rounded" />
                <span className="text-sm text-gray-700">Visible on website</span>
              </label>
            </div>

            <div className="flex gap-3 pt-2">
              <Button data-testid="save-speaker-btn" onClick={handleSave} className="bg-green-600 hover:bg-green-700 flex items-center gap-2">
                <Save size={16} /> {editing ? 'Update Speaker' : 'Add Speaker'}
              </Button>
              <Button variant="outline" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {speakers.map(speaker => (
          <Card key={speaker.id} className={`relative ${!speaker.visible ? 'opacity-50' : ''}`} data-testid={`admin-speaker-${speaker.id}`}>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                  {speaker.photo ? (
                    <img src={speaker.photo} alt={speaker.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User size={24} className="text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-900 truncate">{speaker.name}</h3>
                  {speaker.title && <p className="text-sm text-green-700 truncate">{speaker.title}</p>}
                  {speaker.company && <p className="text-xs text-gray-500">{speaker.company}</p>}
                  {speaker.sub_event && (
                    <span className="inline-block mt-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                      {speaker.sub_event}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4 pt-3 border-t">
                <Button variant="ghost" size="sm" onClick={() => handleEdit(speaker)} className="flex items-center gap-1 text-gray-600">
                  <Edit3 size={14} /> Edit
                </Button>
                <Button variant="ghost" size="sm" onClick={() => toggleVisibility(speaker)} className="flex items-center gap-1 text-gray-600">
                  {speaker.visible ? <EyeOff size={14} /> : <Eye size={14} />}
                  {speaker.visible ? 'Hide' : 'Show'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(speaker.id)} className="flex items-center gap-1 text-red-600 hover:text-red-700 ml-auto">
                  <Trash2 size={14} /> Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {speakers.length === 0 && !showForm && (
        <div className="text-center py-12 text-gray-500">
          <User size={48} className="mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium">No speakers yet</p>
          <p className="text-sm">Click "Add Speaker" to add your first speaker.</p>
        </div>
      )}
    </div>
  );
};
